import { useFilters } from "@/hooks/useFilters";
import { TIME_RANGES, type TimeRangeKey } from "@/config/timeRanges";
import {
  ENDPOINT_AREAS,
  MANAGED_LIST_TYPES,
  ACTIONS,
  OUTCOMES,
  BOT_SCORE_BUCKETS,
  groupsForEndpointAreas,
  selectedEndpointAreas,
  type EndpointAreaKey,
} from "@/config/filters";
import { RISK_BANDS, type RiskBand } from "@/config/riskBands";
import { countActiveFilters } from "@/lib/filterCount";
import { Button } from "../primitives";
import clsx from "clsx";

/**
 * Global filter bar — sits below the page heading on every tab.
 * All values flow through `useFilters` so URL state is shareable and
 * auto-refresh preserves them.
 */
export function FilterBar() {
  const { filters, setFilter, resetFilters } = useFilters();

  const has = countActiveFilters(filters);

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-surface-1 p-2.5 shadow-card">
      <Select
        label="Time"
        value={filters.timeRange}
        onChange={(v) => setFilter("timeRange", v as TimeRangeKey)}
        options={TIME_RANGES.map((r) => ({ value: r.key, label: r.label }))}
      />

      <MultiToggle<RiskBand>
        label="Risk band"
        values={filters.riskBands ?? []}
        options={RISK_BANDS.map((b) => ({ value: b.key, label: b.label, tone: b.tw }))}
        onChange={(v) => setFilter("riskBands", v.length ? v : undefined)}
      />

      <MultiToggle<EndpointAreaKey>
        label="Area"
        values={selectedEndpointAreas(filters.endpointGroups ?? [])}
        options={ENDPOINT_AREAS.map((area) => ({ value: area.key, label: area.label }))}
        onChange={(v) => setFilter("endpointGroups", v.length ? groupsForEndpointAreas(v) : undefined)}
      />

      <Select
        label="Managed list"
        value={filters.managedList}
        onChange={(v) => setFilter("managedList", v as never)}
        options={[{ value: "", label: "any" }, ...MANAGED_LIST_TYPES.map((m) => ({ value: m, label: m }))]}
      />

      <Select
        label="Bot score"
        value={filters.botScoreBucket}
        onChange={(v) => setFilter("botScoreBucket", (v || undefined) as never)}
        options={[{ value: "", label: "any" }, ...BOT_SCORE_BUCKETS.map((b) => ({ value: b.key, label: b.key }))]}
      />

      <Select
        label="Action"
        value={filters.action}
        onChange={(v) => setFilter("action", (v || undefined) as never)}
        options={[{ value: "", label: "any" }, ...ACTIONS.map((a) => ({ value: a, label: a }))]}
      />

      <Select
        label="Outcome"
        value={filters.outcome}
        onChange={(v) => setFilter("outcome", (v || undefined) as never)}
        options={[{ value: "", label: "any" }, ...OUTCOMES.map((o) => ({ value: o, label: o }))]}
      />

      <TextInput label="Country" placeholder="ISO2" value={filters.country ?? ""} onChange={(v) => setFilter("country", v.toUpperCase() || undefined)} maxLength={2} className="w-20" />
      <TextInput label="ASN"     placeholder="13335" value={filters.asn ?? ""} onChange={(v) => setFilter("asn", v || undefined)} className="w-24" />
      <TextInput label="IP"      placeholder="1.2.3.4" value={filters.ip ?? ""} onChange={(v) => setFilter("ip", v || undefined)} className="w-36" />
      <TextInput label="JA4"     placeholder="t13d…" value={filters.ja4 ?? ""} onChange={(v) => setFilter("ja4", v || undefined)} className="w-40" />
      <TextInput label="Email"   placeholder="user@…" value={filters.email ?? ""} onChange={(v) => setFilter("email", v || undefined)} className="w-44" />
      <TextInput label="Business" placeholder="biz id" value={filters.businessId ?? ""} onChange={(v) => setFilter("businessId", v || undefined)} className="w-28" />

      {filters.country && (
        <span className="inline-flex items-center gap-1 rounded-md border border-sev-watch/40 bg-sev-watch/10 px-2 py-0.5 text-xs text-sev-watch">
          country = {filters.country}
          <button type="button" className="ml-1 text-sev-watch hover:text-fg" onClick={() => setFilter("country", undefined)} aria-label="Clear country filter">×</button>
        </span>
      )}

      <div className="ml-auto flex items-center gap-2">
        {has > 0 && <span className="text-xs text-fg-subtle">{has} active</span>}
        <Button variant="ghost" size="sm" onClick={resetFilters} disabled={has === 0}>Reset</Button>
      </div>
    </div>
  );
}

/* --------- inputs ------------------------------------------------------- */

function Select<T extends string>({ label, value, onChange, options }: {
  label: string;
  value?: T;
  onChange: (v: T) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="flex items-center gap-1.5 text-xs">
      <span className="text-fg-subtle">{label}</span>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value as T)}
        className="h-7 rounded-md border border-border-strong bg-surface-2 px-1.5 text-xs text-fg outline-none hover:border-fg-muted"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function TextInput({ label, value, onChange, placeholder, className, maxLength }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; className?: string; maxLength?: number;
}) {
  return (
    <label className="flex items-center gap-1.5 text-xs">
      <span className="text-fg-subtle">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className={clsx(
          "h-7 rounded-md border border-border-strong bg-surface-2 px-2 font-mono text-xs text-fg placeholder-fg-subtle outline-none hover:border-fg-muted",
          className,
        )}
      />
    </label>
  );
}

function MultiToggle<T extends string>({ label, values, options, onChange }: {
  label: string;
  values: T[];
  options: { value: T; label: string; tone?: string }[];
  onChange: (v: T[]) => void;
}) {
  return (
    <div className="flex items-center gap-1.5 text-xs">
      <span className="text-fg-subtle">{label}</span>
      <div className="inline-flex overflow-hidden rounded-md border border-border-strong">
        {options.map((o) => {
          const on = values.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => onChange(on ? values.filter((v) => v !== o.value) : [...values, o.value])}
              className={clsx(
                "px-2 py-1 text-[11px]",
                on ? "bg-surface-3 text-fg" : "text-fg-muted hover:bg-surface-2 hover:text-fg",
              )}
            >{o.label}</button>
          );
        })}
      </div>
    </div>
  );
}
